<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import sessionStorage from 'shared/helpers/sessionStorage';

import BillingCard from './components/BillingCard.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const router = useRouter();
const { t } = useI18n();
const { currentAccount, isBillingEnabled } = useAccount();
const {
  captainEnabled,
  documentLimits,
  responseLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

const isWaitingForBilling = ref(false);

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

const planName = computed(() => {
  return customAttributes.value.plan_name;
});

const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const subscriptionStatus = computed(() => {
  return customAttributes.value.subscription_status || 'active';
});

const accountFeatures = computed(() => currentAccount.value?.features || {});
const accountLimits = computed(() => currentAccount.value?.limits || {});

const planBadgeClass = computed(() => {
  const plan = planName.value?.toLowerCase();
  if (plan === 'enterprise') return 'bg-n-teal-4 text-n-teal-11';
  if (plan === 'business') return 'bg-n-blue-4 text-n-blue-11';
  return 'bg-n-blue-3 text-n-blue-11';
});

const hasFeature = feature => accountFeatures.value[feature] === true;

const hasNumericValue = value => Number.isFinite(Number(value));

const usageColorClass = percent => {
  if (percent >= 95) return 'bg-n-ruby-9';
  if (percent >= 80) return 'bg-n-amber-9';
  return 'bg-n-blue-9';
};

const statusBadgeClass = computed(() => {
  const status = subscriptionStatus.value?.toLowerCase();
  if (status === 'active') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'expired' || status === 'suspended') {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  return 'bg-n-slate-3 text-n-slate-11';
});

const normalizeUsageLimit = limit => {
  if (!limit || typeof limit !== 'object') return null;
  if (!hasNumericValue(limit.consumed)) return null;

  const allowed = Number(limit.total_count ?? limit.allowed ?? 0);
  const consumed = Number(limit.consumed ?? 0);
  if (!Number.isFinite(allowed) || allowed <= 0) return null;

  return {
    consumed,
    totalCount: allowed,
    currentAvailable: Math.max(allowed - consumed, 0),
    percent: Math.min(Math.round((consumed / allowed) * 100), 100),
    colorClass: usageColorClass(
      Math.min(Math.round((consumed / allowed) * 100), 100)
    ),
  };
};

const normalizeIncludedLimit = limit => {
  if (typeof limit === 'number' || typeof limit === 'string') {
    const value = Number(limit);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (!limit || typeof limit !== 'object') return null;
  if (hasNumericValue(limit.consumed)) return null;

  const allowed = Number(limit.total_count ?? limit.allowed ?? 0);
  return Number.isFinite(allowed) && allowed > 0 ? allowed : null;
};

const limitLabels = computed(() => ({
  agents: t('BILLING_SETTINGS.USAGE.USERS'),
  inboxes: t('BILLING_SETTINGS.USAGE.INBOXES'),
  emails: t('BILLING_SETTINGS.USAGE.EMAILS'),
  whatsapp_campaign_messages: t(
    'BILLING_SETTINGS.USAGE.WHATSAPP_CAMPAIGN_MESSAGES'
  ),
}));

const planUsageLimits = computed(() => {
  const usageItems = Object.entries(limitLabels.value).map(([key, title]) => ({
    key,
    title,
    limit: normalizeUsageLimit(accountLimits.value[key]),
  }));

  return usageItems.filter(item => item.limit);
});

const includedLimits = computed(() => {
  const limitItems = Object.entries(limitLabels.value).map(([key, title]) => ({
    key,
    title,
    value: normalizeIncludedLimit(accountLimits.value[key]),
  }));

  return limitItems.filter(item => item.value);
});

const captainUsageLimits = computed(() => {
  const items = [];

  if (captainEnabled.value && responseLimits.value) {
    const limit = normalizeUsageLimit(responseLimits.value);
    if (limit) {
      items.push({
        key: 'captain_responses',
        title: t('BILLING_SETTINGS.CAPTAIN.RESPONSES'),
        limit,
      });
    }
  }

  if (captainEnabled.value && documentLimits.value) {
    const limit = normalizeUsageLimit(documentLimits.value);
    if (limit) {
      items.push({
        key: 'captain_documents',
        title: t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS'),
        limit,
      });
    }
  }

  return items;
});

const usageMeters = computed(() => [
  ...planUsageLimits.value,
  ...captainUsageLimits.value,
]);

const featureGroups = computed(() => {
  const groups = [
    {
      key: 'whatsapp',
      title: t('BILLING_SETTINGS.FEATURES.GROUPS.WHATSAPP'),
      features: [
        {
          key: 'whatsapp_campaign',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.WHATSAPP_CAMPAIGNS'),
        },
      ],
    },
    {
      key: 'channels',
      title: t('BILLING_SETTINGS.FEATURES.GROUPS.CHANNELS'),
      features: [
        {
          key: 'channel_email',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.EMAIL_CHANNEL'),
        },
        {
          key: 'channel_facebook',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.FACEBOOK_CHANNEL'),
        },
        {
          key: 'channel_instagram',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.INSTAGRAM_CHANNEL'),
        },
        {
          key: 'channel_tiktok',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.TIKTOK_CHANNEL'),
        },
      ],
    },
    {
      key: 'team_management',
      title: t('BILLING_SETTINGS.FEATURES.GROUPS.TEAM_MANAGEMENT'),
      features: [
        {
          key: 'team_management',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.TEAM_MANAGEMENT'),
        },
        {
          key: 'custom_roles',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.CUSTOM_ROLES'),
        },
        {
          key: 'companies',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.COMPANIES'),
        },
        {
          key: 'sla',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.SLA'),
        },
      ],
    },
    {
      key: 'automation_ai',
      title: t('BILLING_SETTINGS.FEATURES.GROUPS.AUTOMATION_AI'),
      features: [
        {
          key: 'captain_integration',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.CAPTAIN'),
        },
        {
          key: 'advanced_search',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.ADVANCED_SEARCH'),
        },
        {
          key: 'advanced_assignment',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.ADVANCED_ASSIGNMENT'),
        },
        {
          key: 'custom_tools',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.CUSTOM_TOOLS'),
        },
      ],
    },
    {
      key: 'security',
      title: t('BILLING_SETTINGS.FEATURES.GROUPS.SECURITY'),
      features: [
        {
          key: 'audit_logs',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.AUDIT_LOGS'),
        },
        {
          key: 'saml',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.SAML'),
        },
        {
          key: 'disable_branding',
          label: t('BILLING_SETTINGS.FEATURES.ITEMS.DISABLE_BRANDING'),
        },
      ],
    },
  ];

  return groups
    .map(group => ({
      ...group,
      features: group.features.filter(feature => hasFeature(feature.key)),
    }))
    .filter(group => group.features.length);
});

const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    await store.dispatch('accounts/subscription');
  }
  fetchLimits();
};

const handleBillingPageLogic = async () => {
  if (!isBillingEnabled.value) {
    router.push({ name: 'home' });
    return;
  }

  // Check if we've already attempted a refresh for billing setup
  const billingRefreshAttempted = sessionStorage.get(BILLING_REFRESH_ATTEMPTED);

  // If cloud user, fetch account details first
  await fetchAccountDetails();

  // If still no billing plan after fetch
  if (!hasABillingPlan.value) {
    // If we haven't attempted refresh yet, do it once
    if (!billingRefreshAttempted) {
      isWaitingForBilling.value = true;
      sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);

      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else {
      // We've already tried refreshing, so just show the no billing message
      // Clear the flag for future visits
      sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    }
  } else {
    // Billing plan found, clear any existing refresh flag
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
  }
};

onMounted(handleBillingPageLogic);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem || isWaitingForBilling"
    :loading-message="
      isWaitingForBilling
        ? $t('BILLING_SETTINGS.NO_BILLING_USER')
        : $t('ATTRIBUTES_MGMT.LOADING')
    "
    :no-records-found="!hasABillingPlan && !isWaitingForBilling"
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.CURRENT_PLAN.SECTION_TITLE')"
          :description="$t('BILLING_SETTINGS.CURRENT_PLAN.DESCRIPTION')"
        >
          <div
            v-if="planName || subscribedQuantity || subscriptionStatus"
            class="rounded-lg border border-n-blue-4 bg-n-blue-1 p-4"
          >
            <div
              class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div class="min-w-0">
                <div class="mb-2 flex flex-wrap items-center gap-2">
                  <span
                    v-if="planName"
                    class="inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold"
                    :class="planBadgeClass"
                  >
                    {{ planName }}
                  </span>
                  <span
                    v-if="subscriptionStatus"
                    class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize"
                    :class="statusBadgeClass"
                  >
                    {{ subscriptionStatus }}
                  </span>
                </div>
                <div class="text-sm text-n-slate-11">
                  {{ $t('BILLING_SETTINGS.CURRENT_PLAN.PLAN_NAME') }}
                </div>
              </div>
              <div
                v-if="subscribedQuantity"
                class="rounded-lg border border-n-blue-5 bg-n-solid-1 px-4 py-3 text-start sm:min-w-44"
              >
                <div class="text-xs font-medium uppercase text-n-slate-10">
                  {{ $t('BILLING_SETTINGS.CURRENT_PLAN.USER_COUNT') }}
                </div>
                <div class="mt-1 text-2xl font-semibold text-n-slate-12">
                  {{ subscribedQuantity }}
                </div>
              </div>
            </div>
          </div>
        </BillingCard>
        <BillingCard
          v-if="usageMeters.length || includedLimits.length"
          :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              flushed
              slate
              icon="i-lucide-refresh-cw"
              :is-loading="isFetchingLimits"
              @click="fetchLimits"
            >
              {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
            </ButtonV4>
          </template>
          <div v-if="usageMeters.length" class="grid gap-4 px-5">
            <div
              v-for="item in usageMeters"
              :key="item.key"
              class="rounded-lg border border-n-weak bg-n-alpha-1 p-4"
            >
              <div
                class="flex gap-4 items-center justify-between text-xs uppercase text-n-slate-10"
              >
                <div class="font-medium tracking-wider">
                  {{ item.title }}
                </div>
                <div class="tabular-nums">
                  {{
                    $t('BILLING_SETTINGS.USAGE.SUMMARY', {
                      consumed: item.limit.consumed,
                      total: item.limit.totalCount,
                      remaining: item.limit.currentAvailable,
                    })
                  }}
                </div>
              </div>
              <div
                class="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-n-blue-3 ring-1 ring-n-blue-4"
              >
                <div
                  class="h-2.5 rounded-full transition-all"
                  :class="item.limit.colorClass"
                  :style="{ width: `${item.limit.percent}%` }"
                />
              </div>
            </div>
          </div>
          <div
            v-if="includedLimits.length"
            class="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 px-5"
            :class="{ 'pt-4': usageMeters.length }"
          >
            <div
              v-for="item in includedLimits"
              :key="item.key"
              class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
            >
              <div class="text-xs text-n-slate-11">
                {{ item.title }}
              </div>
              <div class="mt-1 text-lg font-medium text-n-slate-12">
                {{ item.value }}
              </div>
              <div class="mt-1 text-xs text-n-slate-10">
                {{ $t('BILLING_SETTINGS.USAGE.INCLUDED_LIMIT') }}
              </div>
            </div>
          </div>
        </BillingCard>
        <BillingCard
          v-if="featureGroups.length"
          :title="$t('BILLING_SETTINGS.FEATURES.TITLE')"
          :description="$t('BILLING_SETTINGS.FEATURES.DESCRIPTION')"
        >
          <div class="grid gap-4 px-5 md:grid-cols-2">
            <div
              v-for="group in featureGroups"
              :key="group.key"
              class="rounded-lg border border-n-blue-4 bg-n-blue-1 p-4"
            >
              <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
                {{ group.title }}
              </h3>
              <div class="grid gap-2">
                <span
                  v-for="feature in group.features"
                  :key="feature.key"
                  class="inline-flex items-center gap-2 rounded-md border border-n-blue-4 bg-n-solid-1 px-3 py-2 text-sm font-medium text-n-slate-11"
                >
                  <span class="i-lucide-check size-4 text-n-blue-9" />
                  {{ feature.label }}
                </span>
              </div>
            </div>
          </div>
        </BillingCard>
      </section>
    </template>
  </SettingsLayout>
</template>
